import {
  ChangeEventHandler,
  FC,
  FocusEventHandler,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { FieldGroup } from 'internalLibrary';
import { FieldMetaProps, useField } from 'formik';
import { LENGHT_FIELD_VALIDATION } from '../../scenes/first-loan/RegisterScene/RegisterForm/useValidationSchema';
import classes from 'classnames';
import styles from './Input.module.scss';

export type DoubleInputProps = {
  label: string;
  dataTest?: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  preName: string;
  postName: string;
  disabled?: boolean;
  helperText?: string;
  externalError: string;
};

const DoubleInput: FC<DoubleInputProps> = (props) => {
  const {
    label,
    preName,
    postName,
    dataTest,
    onChange,
    onBlur,
    onFocus,
    disabled = false,
    helperText,
    externalError,
  } = props;

  const [preField, preMeta, preFieldHelpers] = useField(preName);
  const [postField, postMeta, postFieldHelpers] = useField(postName);
  const [errorFromOutside, setErrorFromOutside] = useState(externalError);

  useEffect(() => {
    setErrorFromOutside(externalError);
  }, [externalError]);

  const handleFocus = useCallback<FocusEventHandler<HTMLInputElement>>(
    (e) => {
      onFocus?.(e);
    },
    [onFocus],
  );

  const valueChanged: ChangeEventHandler<HTMLInputElement> = (e) => {
    const { name, value } = e.target;

    const lengthPreName =
      preName in LENGHT_FIELD_VALIDATION
        ? LENGHT_FIELD_VALIDATION[preName]
        : undefined;
    const lengthPostName =
      postName in LENGHT_FIELD_VALIDATION
        ? LENGHT_FIELD_VALIDATION[postName]
        : undefined;

    const valueWithDash = value.includes('-');
    const valueCleaned = value.replace('-', '');

    // In case user copy&paste the whole value in the same field
    if (
      valueWithDash ||
      (lengthPreName &&
        lengthPostName &&
        valueCleaned.length === lengthPreName + lengthPostName)
    ) {
      const nextPreValue = valueWithDash
        ? value.split('-')[0]
        : valueCleaned.substring(0, lengthPreName);

      let nextPostValue;
      if (valueWithDash) {
        nextPostValue = value.split('-')[1];
      } else {
        nextPostValue = lengthPostName
          ? valueCleaned.substring(lengthPostName, 0)
          : valueCleaned;
      }

      preFieldHelpers.setValue(nextPreValue);
      postFieldHelpers.setValue(nextPostValue);
    } else {
      if (name === preName) preFieldHelpers.setValue(value);
      if (name === postName) postFieldHelpers.setValue(value);
    }

    setErrorFromOutside('');

    onChange?.(e);
  };

  const inputBlurred: FocusEventHandler<HTMLInputElement> = (e) => {
    if (e.target.name === preName) preFieldHelpers.setTouched(true);
    else postFieldHelpers.setTouched(true);

    onBlur?.(e);
  };

  const validationText =
    resolveValidationText(preMeta, postMeta) || errorFromOutside;

  return (
    <FieldGroup
      validationText={validationText}
      dataTestValidationText={`field-group-validation_${postName}`}
      helperText={helperText || ''}
      labelText={label}
    >
      <div className={classes(styles.container)}>
        <input
          {...preField}
          type="text"
          data-test={dataTest}
          id={preName}
          onChange={valueChanged}
          onBlur={inputBlurred}
          onFocus={handleFocus}
          className={classes(
            styles.input,
            ((preMeta.touched && preMeta.error) || errorFromOutside) &&
              styles.danger,
            styles.prefix,
          )}
          placeholder=" "
          disabled={disabled}
        />
        <div className={styles.dash}>-</div>
        <input
          {...postField}
          type="text"
          data-test={dataTest}
          id={postName}
          onChange={valueChanged}
          onBlur={inputBlurred}
          onFocus={handleFocus}
          className={classes(
            styles.input,
            ((postMeta.touched && postMeta.error) || errorFromOutside) &&
              styles.danger,
          )}
          placeholder=" " // NOTE: Used for detecting if input has value
          disabled={disabled}
        />
      </div>
    </FieldGroup>
  );
};

export default DoubleInput;

const resolveValidationText = (
  preMeta: FieldMetaProps<string>,
  postMeta: FieldMetaProps<string>,
): string => {
  if (preMeta.touched && preMeta.error) return preMeta.error;
  if (postMeta.touched && postMeta.error) return postMeta.error;
  else return '';
};
